import React, { useCallback, useEffect, useRef, useState } from "react";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { autoScrollForElements } from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";
import { format, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { AddAppointmentModal } from "components/AddAppointment/AddAppointmentModal";
import { AppointmentsItems } from "components/AppointmentsList/components/AppointmentsItems/AppointmentsItems";
import { TimeSlots } from "components/AppointmentsList/components/TimeSlots/TimeSlots";

import styles from "./AppointmentsBoardView.module.css";
import { useAutoScrollToCurrentTime } from "./useAutoScrollToCurrentTime";

export const AppointmentsBoardView = ({
  data,
  timeSlots,
  isSmall,
  onAppointmentClick,
  showAddAppointmentModal,
  setShowAddAppointmentModal,
  selectedAppointments,
  setSelectedAppointments,
  slotDurationMinutes,
  multiDay = false,
  maxHeight = "calc(100dvh - 360px)",
  hideTimeIndicator = false,
}) => {
  const mainScrollRef = useRef(null);
  const [scrollContainerWidth, setScrollContainerWidth] = useState(0);
  const verticalTrackRef = useRef(null);
  const isDraggingVerticalThumb = useRef(false);
  const verticalDragStartY = useRef(0);
  const verticalDragStartScrollTop = useRef(0);
  const [verticalThumbStyle, setVerticalThumbStyle] = useState({
    top: 0,
    height: 0,
  });
  const [hasVerticalOverflow, setHasVerticalOverflow] = useState(false);

  useAutoScrollToCurrentTime(mainScrollRef, { timeSlots, slotDurationMinutes });

  const updateVerticalThumb = useCallback(() => {
    const el = mainScrollRef.current;
    const track = verticalTrackRef.current;
    if (!el) return;
    const overflow = el.scrollHeight > el.clientHeight;
    setHasVerticalOverflow(overflow);
    if (!track || !overflow) return;
    const ratio = el.clientHeight / el.scrollHeight;
    const thumbHeight = Math.max(track.clientHeight * ratio, 40);
    const maxThumbTop = track.clientHeight - thumbHeight;
    const scrollRatio = el.scrollTop / (el.scrollHeight - el.clientHeight);
    setVerticalThumbStyle({
      top: scrollRatio * maxThumbTop,
      height: thumbHeight,
    });
  }, []);

  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    updateVerticalThumb();
    el.addEventListener("scroll", updateVerticalThumb);
    const observer = new ResizeObserver(() => {
      updateVerticalThumb();
      setScrollContainerWidth(el.scrollWidth);
    });
    setScrollContainerWidth(el.scrollWidth);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateVerticalThumb);
      observer.disconnect();
    };
  }, [updateVerticalThumb]);

  useEffect(() => {
    if (hasVerticalOverflow) requestAnimationFrame(updateVerticalThumb);
  }, [hasVerticalOverflow, updateVerticalThumb]);

  const handleVerticalThumbMouseDown = (e) => {
    e.preventDefault();
    isDraggingVerticalThumb.current = true;
    verticalDragStartY.current = e.clientY;
    verticalDragStartScrollTop.current = mainScrollRef.current?.scrollTop ?? 0;
    const onMouseMove = (moveEvent) => {
      if (
        !isDraggingVerticalThumb.current ||
        !mainScrollRef.current ||
        !verticalTrackRef.current
      )
        return;
      const dy = moveEvent.clientY - verticalDragStartY.current;
      const el = mainScrollRef.current;
      const track = verticalTrackRef.current;
      const scrollRatio =
        (el.scrollHeight - el.clientHeight) /
        (track.clientHeight - verticalThumbStyle.height);
      el.scrollTop = verticalDragStartScrollTop.current + dy * scrollRatio;
    };
    const onMouseUp = () => {
      isDraggingVerticalThumb.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const handleVerticalTrackClick = (e) => {
    if (!mainScrollRef.current || !verticalTrackRef.current) return;
    const el = mainScrollRef.current;
    const track = verticalTrackRef.current;
    const clickY = e.clientY - track.getBoundingClientRect().top;
    el.scrollTop =
      (clickY / track.clientHeight) * (el.scrollHeight - el.clientHeight);
  };

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [appointmentType, setAppointmentType] = useState();
  const [, setDraggedItem] = useState(null);
  const [, setIsDragging] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [thumbStyle, setThumbStyle] = useState({ left: 0, width: 0 });
  const trackRef = useRef(null);
  const isDraggingThumb = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScrollLeft = useRef(0);

  useEffect(() => {
    let monitorCleanup;
    let autoScrollCleanup;

    try {
      monitorCleanup = monitorForElements({
        onDragStart: ({ source }) => {
          setDraggedItem(source.data);
          setIsDragging(true);
        },
        onDrop: ({ source, location }) => {
          try {
            const destination = location.current.dropTargets[0];

            if (!destination) {
              setIsDragging(false);
              setDraggedItem(null);
              return;
            }

            const draggedData = source.data;
            const targetData = destination.data;

            if (source.element === destination.element) {
              setIsDragging(false);
              setDraggedItem(null);
              return;
            }

            if (draggedData.item && targetData.item) {
              const oldItem = draggedData.item.meeting;
              const targetDoctor = targetData.doctor;

              setSelectedAppointment({
                ...targetData.item,
                meeting: oldItem,
              });
              setAppointmentType("appointment");
              setSelectedDoctor(targetDoctor);
              setShowAddAppointmentModal(true);
            }

            setIsDragging(false);
            setDraggedItem(null);
          } catch (error) {
            console.warn("Error handling drop event:", error);
            setIsDragging(false);
            setDraggedItem(null);
          }
        },
        onDragEnd: () => {
          setIsDragging(false);
          setDraggedItem(null);
        },
      });

      autoScrollCleanup = autoScrollForElements({
        element: mainScrollRef.current || document.documentElement,
        canScroll: ({ source }) => {
          return source.data.type === "appointment";
        },
        getScrollElement: () =>
          mainScrollRef.current || document.documentElement,
      });
    } catch (error) {
      console.warn("Failed to setup drag and drop monitor:", error);
      return;
    }

    return () => {
      if (typeof monitorCleanup === "function") {
        try {
          monitorCleanup();
        } catch (error) {
          console.warn("Failed to cleanup monitor:", error);
        }
      }
      if (typeof autoScrollCleanup === "function") {
        try {
          autoScrollCleanup();
        } catch (error) {
          console.warn("Failed to cleanup auto scroll:", error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateScrollButtons = useCallback(() => {
    const el = mainScrollRef.current;
    const track = trackRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth;
    setHasOverflow(overflow);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    if (track && overflow) {
      const ratio = el.clientWidth / el.scrollWidth;
      const thumbWidth = Math.max(track.clientWidth * ratio, 40);
      const maxThumbLeft = track.clientWidth - thumbWidth;
      const scrollRatio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
      setThumbStyle({ left: scrollRatio * maxThumbLeft, width: thumbWidth });
    } else {
      setThumbStyle({ left: 0, width: trackRef.current?.clientWidth ?? 0 });
    }
  }, []);

  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);
    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      observer.disconnect();
    };
  }, [updateScrollButtons]);

  useEffect(() => {
    if (hasOverflow) {
      requestAnimationFrame(updateScrollButtons);
    }
  }, [hasOverflow, updateScrollButtons]);

  const scrollBy = (amount) => {
    mainScrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  const handleGridMouseDown = useCallback((e) => {
    const el = mainScrollRef.current;
    if (!el) return;
    const startX = e.clientX;
    const startScrollLeft = el.scrollLeft;
    let moved = false;

    const onMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      if (moved) {
        el.scrollLeft = startScrollLeft - dx;
        moveEvent.preventDefault();
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      if (moved) el.style.cursor = "default";
    };

    el.style.cursor = "grabbing";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, []);

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    isDraggingThumb.current = true;
    dragStartX.current = e.clientX;
    dragStartScrollLeft.current = mainScrollRef.current?.scrollLeft ?? 0;

    const onMouseMove = (moveEvent) => {
      if (
        !isDraggingThumb.current ||
        !mainScrollRef.current ||
        !trackRef.current
      )
        return;
      const dx = moveEvent.clientX - dragStartX.current;
      const track = trackRef.current;
      const el = mainScrollRef.current;
      const scrollRatio =
        (el.scrollWidth - el.clientWidth) /
        (track.clientWidth - thumbStyle.width);
      el.scrollLeft = dragStartScrollLeft.current + dx * scrollRatio;
    };

    const onMouseUp = () => {
      isDraggingThumb.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const handleTrackClick = (e) => {
    if (!mainScrollRef.current || !trackRef.current) return;
    const track = trackRef.current;
    const el = mainScrollRef.current;
    const clickX = e.clientX - track.getBoundingClientRect().left;
    const scrollRatio = clickX / track.clientWidth;
    el.scrollLeft = scrollRatio * (el.scrollWidth - el.clientWidth);
  };

  const isOneDoctor = !multiDay && data[0]?.doctors?.length === 1;

  const columnWidth = isSmall ? "200px" : "300px";

  const columns = multiDay
    ? data
        .filter((day) => day.doctors.length > 0)
        .map((day, index) => ({
          key: `${day.date || "day"}-${index}`,
          doctor: day.doctors[0],
          rowDate: day.date,
          label: format(parseISO(day.date), "EEE d MMM"),
          dayTimeSlots: timeSlots,
        }))
    : (data[0]?.doctors ?? []).map((doctor, index) => ({
        key: `${doctor.id || "doctor"}-${index}`,
        doctor,
        rowDate: undefined,
        label: `${doctor.firstName} ${doctor.lastName}`,
        role: doctor.staffRole,
        active: doctor.active,
        dayTimeSlots: timeSlots,
      }));

  const handleCheckboxToggle = useCallback(
    (appointmentId) => {
      setSelectedAppointments((prev) => {
        const newSelected = { ...prev };
        if (newSelected[appointmentId]) {
          delete newSelected[appointmentId];
        } else {
          newSelected[appointmentId] = true;
        }
        return newSelected;
      });
    },
    [setSelectedAppointments]
  );

  const timeSlotsWidth = isSmall ? 130 : 200;
  const slotH = slotDurationMinutes === 5 ? "45px" : "60px";

  return (
    <div className={styles.boardViewOuter}>
      {hasOverflow && (
        <div
          className={styles.boardGridControls}
          style={{ paddingLeft: timeSlotsWidth + 10 }}
        >
          <ChevronLeft
            className={`${styles.scrollBtn} ${
              !canScrollLeft ? styles.scrollBtnDisabled : ""
            }`}
            onClick={() => scrollBy(-300)}
          />
          <div
            ref={trackRef}
            className={styles.scrollTrack}
            onClick={handleTrackClick}
          >
            <div
              className={styles.scrollThumb}
              style={{ left: thumbStyle.left, width: thumbStyle.width }}
              onMouseDown={handleThumbMouseDown}
            />
          </div>
          <ChevronRight
            className={`${styles.scrollBtn} ${
              !canScrollRight ? styles.scrollBtnDisabled : ""
            }`}
            onClick={() => scrollBy(300)}
          />
        </div>
      )}

      <div className={styles.verticalScrollWrapper}>
        <div
          ref={mainScrollRef}
          className={styles.singleScrollContainer}
          style={maxHeight ? { maxHeight } : undefined}
          onMouseDown={handleGridMouseDown}
        >
          {/* Time column — sticky left inside the single scroll container */}
          <div
            className={styles.timeSlotsColumn}
            style={{ width: timeSlotsWidth }}
          >
            <div className={styles.timeSlotsHeaderSpacer} />
            <TimeSlots
              timeSlots={timeSlots}
              slotDurationMinutes={slotDurationMinutes}
              showTimeIndicator={!hideTimeIndicator}
              indicatorWidth={scrollContainerWidth}
            />
          </div>

          <div className={styles.doctorColumnsArea}>
            {columns.map(
              (
                { key, doctor, rowDate, label, role, active, dayTimeSlots },
                i
              ) => (
                <React.Fragment key={key}>
                  {i > 0 && <div className={styles.colDivider} />}
                  <div
                    className={styles.doctorColumn}
                    style={{
                      minWidth: columnWidth,
                      ...(isOneDoctor && { flex: 1, maxWidth: "none" }),
                    }}
                  >
                    <div className={styles.doctorHeaderCard}>
                      <div className={styles.doctorHeaderContent}>
                        <p className={styles.doctorName}>
                          {!multiDay && active && (
                            <span
                              style={{
                                display: "inline-block",
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: "#22c55e",
                                marginRight: "4px",
                              }}
                            />
                          )}
                          {label}
                          {!multiDay && role && (
                            <span
                              style={{
                                fontWeight: "normal",
                                marginLeft: "4px",
                              }}
                            >
                              ({role.toLowerCase()})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        // minmax(0, 1fr) (not "1fr") stops the column track from
                        // growing to a card's min-content width and overflowing
                        // the doctor column; cards clip to the container instead.
                        gridTemplateColumns: "minmax(0, 1fr)",
                        gridTemplateRows: `repeat(${dayTimeSlots?.length}, ${slotH})`,
                        gap: "12px",
                        marginTop: "12px",
                        minWidth: 0,
                      }}
                    >
                      <AppointmentsItems
                        doctor={doctor}
                        timeSlots={dayTimeSlots}
                        rowDate={rowDate}
                        setSelectedAppointment={setSelectedAppointment}
                        setSelectedDoctor={setSelectedDoctor}
                        setShowAddAppointmentModal={setShowAddAppointmentModal}
                        setAppointmentType={setAppointmentType}
                        onAppointmentClick={onAppointmentClick}
                        selectedAppointments={selectedAppointments}
                        onCheckboxToggle={handleCheckboxToggle}
                        slotDurationMinutes={slotDurationMinutes}
                      />
                    </div>
                  </div>
                </React.Fragment>
              )
            )}
          </div>
        </div>

        <div
          ref={verticalTrackRef}
          className={styles.verticalScrollTrack}
          style={maxHeight === "auto" ? { visibility: "hidden" } : undefined}
          onClick={handleVerticalTrackClick}
        >
          {hasVerticalOverflow && (
            <div
              className={styles.verticalScrollThumb}
              style={{
                top: verticalThumbStyle.top,
                height: verticalThumbStyle.height,
              }}
              onMouseDown={handleVerticalThumbMouseDown}
            />
          )}
        </div>
      </div>

      <AddAppointmentModal
        setAppointmentType={setAppointmentType}
        isOpen={showAddAppointmentModal}
        closeModal={() => {
          setShowAddAppointmentModal(false);
          setSelectedAppointment(null);
        }}
        initialData={
          appointmentType === "appointment"
            ? selectedAppointment?.meeting?.consultation
            : selectedAppointment?.meeting?.exception
        }
        // When the modal opens from a doctor-specific column we pass
        // that doctor's id so the form pre-fills with them. From any
        // non-column context we deliberately do NOT fall back to the
        // logged-in user — the form will pick the first doctor in the
        // dropdown instead.
        staffId={selectedDoctor?.staffMember?.id || selectedDoctor?.id}
        startDate={
          selectedAppointment?.start &&
          format(selectedAppointment?.start, "dd-MM-yyyy")
        }
        startTime={
          selectedAppointment?.formattedRange?.split(" -")?.[0] ||
          (selectedAppointment?.start &&
            format(selectedAppointment.start, "HH:mm"))
        }
        allTimes={timeSlots}
        defaultAppointmentType={appointmentType}
        slotDurationMinutes={slotDurationMinutes}
      />
    </div>
  );
};
